import React, { useState, useEffect, useRef } from 'react';
import * as api from '../services/api';
import { generateReport } from '../services/reportExport';

export default function Dashboard({ showToast, currentUser }) {
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [expiringAgreements, setExpiringAgreements] = useState([]);
  const [casesByStage, setCasesByStage] = useState([]);
  const [complianceScore, setComplianceScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportDataRef = useRef({});

  useEffect(() => { loadDashboard(); }, []);

  async function loadDashboard() {
    try {
      const [kpiRes, alertRes, reqRes, expRes, caseRes, compRes] = await Promise.all([
        api.getDashboardKPIs(), api.getDashboardAlerts(), api.getRecentRequests(),
        api.getExpiringAgreements(), api.getCasesByStage(), api.getComplianceScore(),
      ]);
      setKpis(kpiRes.data); setAlerts(alertRes.data); setRecentRequests(reqRes.data);
      setExpiringAgreements(expRes.data); setCasesByStage(caseRes.data); setComplianceScore(compRes.data);
      // Cache for report export
      reportDataRef.current = {
        kpis: kpiRes.data, alerts: alertRes.data, recentRequests: reqRes.data,
        expiringAgreements: expRes.data, casesByStage: caseRes.data, complianceScore: compRes.data,
      };
    } catch (err) { showToast('Failed to load dashboard'); }
    finally { setLoading(false); }
  }

  // FR-075 / FR-076: Report export
  async function handleExport(level) {
    let exportData = { ...reportDataRef.current };
    if (level === 'detailed') {
      try {
        showToast('Gathering full report data...');
        const [reqRes, ittRes, sigRes] = await Promise.all([
          api.getRequests({ limit: 500 }),
          api.getITTRecords(),
          api.getSignatureLog(),
        ]);
        exportData.allRequests = reqRes.data.data || reqRes.data || [];
        exportData.ittRecords = ittRes.data || [];
        exportData.signatureLog = sigRes.data || [];
      } catch (err) { /* proceed with available data */ }
    }
    generateReport(level, exportData, currentUser);
    showToast(`${level === 'detailed' ? 'Detailed' : 'High-Level'} report downloaded`);
  }

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const statusBadge = (s) => {
    const c = { pending:'badge-blue', in_review:'badge-gold', completed:'badge-green', overdue:'badge-red' };
    return <span className={`badge ${c[s]||'badge-gray'}`}><span className="dot"></span> {s?.replace(/_/g,' ')}</span>;
  };
  const priorityBadge = (p) => {
    const c = { low:'badge-gray', medium:'badge-gold', high:'badge-red', urgent:'badge-red' };
    return <span className={`badge ${c[p]||'badge-gray'}`}>{p}</span>;
  };

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Dashboard Overview</div>
        <div style={{display:'flex',gap:8}}>
          <button className="topbar-btn btn-ghost" onClick={() => handleExport('high')} title="Executive summary: KPIs and alerts only">
            ⬇ High-Level Report
          </button>
          <button className="topbar-btn btn-primary" onClick={() => handleExport('detailed')} title="Full report with all tables and data">
            ⬇ Detailed Report
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-icon blue">📋</div><div><div className="stat-num">{kpis?.total_requests_ytd||0}</div><div className="stat-label">Total Requests ({new Date().getFullYear()})</div><div className="stat-delta up">↑ {kpis?.requests_this_month||0} this month</div></div></div>
        <div className="stat-card"><div className="stat-icon green">✅</div><div><div className="stat-num">{kpis?.completed_requests_ytd||0}</div><div className="stat-label">Completed Requests</div><div className="stat-delta up">↑ {kpis?.total_requests_ytd ? Math.round((kpis.completed_requests_ytd/kpis.total_requests_ytd)*100) : 0}% completion</div></div></div>
        <div className="stat-card"><div className="stat-icon gold">⏳</div><div><div className="stat-num">{kpis?.expiring_agreements||0}</div><div className="stat-label">Agreements Expiring Soon</div></div></div>
        <div className="stat-card"><div className="stat-icon red">⚖️</div><div><div className="stat-num">{kpis?.active_cases||0}</div><div className="stat-label">Active Legal Cases</div></div></div>
      </div>

      {/* Alerts */}
      {alerts?.expiringAgreements > 0 && <div className="alert alert-gold">⚠️ <strong>Action Needed:</strong> {alerts.expiringAgreements} agreement(s) expire within 30 days.</div>}
      {alerts?.pendingReviews > 0 && <div className="alert alert-red">🔴 <strong>Urgent:</strong> {alerts.pendingReviews} review(s) pending over 5 business days.</div>}
      {alerts?.overdueRequests > 0 && <div className="alert alert-red">🔴 <strong>Overdue:</strong> {alerts.overdueRequests} request(s) have exceeded SLA.</div>}

      {/* Recent Requests + Expiring Agreements */}
      <div className="row-2">
        <div className="card">
          <div className="card-title">📋 Recent Requests</div>
          <table>
            <thead><tr><th>Ref</th><th>Subject</th><th>Type</th><th>Priority</th><th>Status</th></tr></thead>
            <tbody>
              {recentRequests.slice(0,6).map(r => (
                <tr key={r.id}>
                  <td style={{fontSize:11,fontWeight:600,color:'var(--mid)'}}>{r.ref}</td>
                  <td>{r.subject}</td>
                  <td style={{fontSize:11}}>{r.type?.replace(/_/g,' ')}</td>
                  <td>{priorityBadge(r.priority)}</td>
                  <td>{statusBadge(r.status)}</td>
                </tr>
              ))}
              {recentRequests.length===0 && <tr><td colSpan={5} style={{textAlign:'center',color:'var(--muted)'}}>No requests yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="card">
          <div className="card-title">⏳ Agreements Expiring Soon</div>
          <div className="expiry-bar">
            {expiringAgreements.map(a => {
              const days = parseInt(a.days_remaining);
              const pct = Math.min(100, (days / 90) * 100);
              const cls = days <= 30 ? 'dn' : days <= 60 ? 'warn' : 'ok';
              return (
                <div className="exp-row" key={a.id}>
                  <div className="exp-label">{a.title}</div>
                  <div className="exp-pbar"><div className="pbar-wrap"><div className="pbar" style={{width:`${pct}%`, background: days<=30?'linear-gradient(90deg,#DC2626,#F87171)':days<=60?'linear-gradient(90deg,#D97706,#FCD34D)':undefined}}></div></div></div>
                  <div className={`exp-days ${cls}`}>{days} days</div>
                </div>
              );
            })}
            {expiringAgreements.length===0 && <p style={{fontSize:13,color:'var(--muted)'}}>No agreements expiring within 90 days</p>}
          </div>
        </div>
      </div>

      {/* Bottom row: Cases + Compliance + Signature Activity */}
      <div className="row-3">
        <div className="card">
          <div className="card-title">⚖️ Cases by Stage</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {casesByStage.map(c => (
              <div key={c.stage}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:4}}>
                  <span>{c.stage?.replace(/_/g,' ')}</span><strong>{c.count}</strong>
                </div>
                <div className="pbar-wrap"><div className="pbar" style={{width:`${Math.min(100,(c.count/Math.max(...casesByStage.map(s=>parseInt(s.count)),1))*100)}%`}}></div></div>
              </div>
            ))}
            {casesByStage.length===0 && <p style={{fontSize:13,color:'var(--muted)'}}>No active cases</p>}
          </div>
        </div>

        <div className="card">
          <div className="card-title">🛡️ Compliance Score</div>
          {complianceScore?.overall ? (
            <div>
              <div style={{textAlign:'center',padding:'16px 0'}}>
                <div style={{fontSize:36,fontWeight:700,color: (complianceScore.overall.overall_score||0) >= 80 ? 'var(--success)' : (complianceScore.overall.overall_score||0) >= 60 ? 'var(--warn)' : 'var(--danger)'}}>{complianceScore.overall.overall_score||0}%</div>
                <div style={{fontSize:11,color:'var(--muted)'}}>Overall Compliance</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {(complianceScore.byCategory||[]).map(cat => (
                  <div key={cat.category}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3}}>
                      <span>{cat.category?.replace(/_/g,' ')}</span><strong>{cat.score_pct||0}%</strong>
                    </div>
                    <div className="pbar-wrap"><div className="pbar" style={{width:`${cat.score_pct||0}%`,background:cat.score_pct>=80?'linear-gradient(90deg,#059669,#34D399)':cat.score_pct>=60?'linear-gradient(90deg,#D97706,#FCD34D)':'linear-gradient(90deg,#DC2626,#F87171)'}}></div></div>
                  </div>
                ))}
              </div>
            </div>
          ) : <p style={{fontSize:13,color:'var(--muted)'}}>No compliance data</p>}
        </div>

        <div className="card">
          <div className="card-title">📊 Quick Stats</div>
          <div style={{display:'flex',flexDirection:'column',gap:12,marginTop:4}}>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
              <span style={{color:'var(--muted)'}}>Overdue Requests</span>
              <strong style={{color: (kpis?.overdue_requests||0) > 0 ? 'var(--danger)' : 'var(--success)'}}>{kpis?.overdue_requests||0}</strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
              <span style={{color:'var(--muted)'}}>Active Cases</span>
              <strong>{kpis?.active_cases||0}</strong>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',fontSize:13}}>
              <span style={{color:'var(--muted)'}}>Expiring Agreements (90d)</span>
              <strong style={{color: (kpis?.expiring_agreements||0) > 0 ? 'var(--warn)' : 'var(--success)'}}>{kpis?.expiring_agreements||0}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
